import {
  extractID,
  type EpisodeID,
  type ProviderNetworkMemberID,
  type ProviderNetworkVisitID,
} from "@repo/db/branded-types";
import { byBrand } from "@repo/tailwind-config/brand";

/**
 * Type-safe routes for the Kinsa app
 *
 * Consolidated URL structure:
 *   /home              — paywall, checkout, confirm-subscription, privacy, welcome, visit-list, select-patient, block-visit
 *   /intake/:patientId — create-patient, chief-complaint, questionnaire
 *   /visit/:patientId/:caseId — chat, summary
 */

export const Routes = {
  // Core consolidated routes
  HOME: "/home",
  INTAKE: "/intake",
  VISIT: "/visit",
  NOTETAKER: "/notetaker",
  NOTETAKER_NEW: "/notetaker/new",
  NOTETAKER_SETTINGS: "/notetaker/settings",
  WHATS_GOING_AROUND: "/whats-going-around",
  WHATS_GOING_AROUND_ABOUT: "/whats-going-around/about",

  // Utility routes
  TOKEN_SYNC: "/token-sync",
  TOKEN_REFRESH_ERROR: "/token-refresh-error",
  ERROR: "/error",
  UNAUTHORIZED: "/unauthorized",
  VALUE_PROPS: "/value-props",

  // Widget routes
  TRIAGED_CTA: "/triaged-cta",
  DISMISSABLE_CTA: "/dismissable-cta",
  INTAKE_RESULTS: "/intake-results",

  /**
   * @deprecated Legacy aliases — remove after all consumers migrate to Routes.HOME / Routes.VISIT.
   * Consumers: determineCaseStatus.ts, patient-selection-content.tsx, chat/actions.ts,
   * create-patient/form.tsx, questionnaire/component.tsx, StartVisitButtonFooter.tsx, value-props/page.tsx
   */
  VISIT_LIST: "/home",
  HOME_CHECKOUT: "/home",
  PAYWALL: "/paywall",
  CHAT: "/visit",
  SELECT_PATIENT: "/home",
  BLOCK_VISIT: "/home",
  VISIT_SUMMARY: "/visit",
} as const;

/** Helper to generate the Notetaker transcript URL for a visit */
export const getNotetakerVisitUrl = (visitId: EpisodeID): string =>
  `${Routes.NOTETAKER}/${extractID(visitId)}`;

/**
 * Helper to generate visit URLs with patient-id and case-id
 */
export const getVisitUrl = (
  patientId: ProviderNetworkMemberID,
  caseId: ProviderNetworkVisitID
): string => {
  return `/visit/${patientId}/${caseId}`;
};

// TODO: fallback value here just for Playwright runner, need to move CONST out of routes
export const TOS_ROOT =
  process.env.NEXT_PUBLIC_TOS_URL ??
  byBrand({
    owlet: "https://owlet.felt.care/tos",
    kinsa: "https://kinsa.framer.website/tos",
  });
export const TOS_CARE = process.env.NEXT_PUBLIC_TOS_CARE_URL ?? TOS_ROOT;
export const TOS_PROVIDER =
  process.env.NEXT_PUBLIC_TOS_PROVIDER_URL ?? TOS_ROOT;
export const TOS_PRIVACY = process.env.NEXT_PUBLIC_TOS_PRIVACY_URL ?? TOS_ROOT;
